import { ApiService } from "./api-services/ApiService";
import { User } from '../types/api';
import socketClusterService from './unifiedSocketService';
import { logger } from '../utils/logger.js';
import { StorageUtils } from '../utils/StorageUtils.js';

declare const localStorage: Storage;

// const API_URL = "/api/auth"; // ✅ Cohérence backend EKOSE-API (non utilisé pour l'instant)

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterRequest {
  name: string; // ✅ Backend utilise 'name'
  email: string;
  password: string;
  role: "PATIENT" | "DOCTOR" | "ADMIN";
  phone?: string;
  phoneNumber?: string;
  residence?: string;
  city?: string;
  region?: string;
  speciality?: string;
  specialty?: string;
  license_number?: string;
  licenseNumber?: string;
  date_of_birth?: string;
  dateOfBirth?: string;
  blood_type?: string;
  bloodType?: string;
  gender?: string;
  confirmPassword?: string;
}

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  user: User;
  expiresIn?: number;
  csrfToken?: string;
}

export interface MFASetupResponse {
  qrCode: string;
  secret: string;
  backupCodes: string[];
}

export interface MFAVerificationRequest {
  token: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirmRequest {
  token: string;
  newPassword: string;
}

export interface ApiErrorResponse {
  response?: {
    data?: {
      error?: string;
      message?: string;
    };
  };
}

class AuthService {
  private static instance: AuthService;
  private token: string | null = null;
  private user: User | null = null;
  private isLoggingOut = false;
  private isRefreshing = false;
  private currentUserPromise: Promise<User | null> | null = null;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Connexion utilisateur
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      logger.debug('[AuthService] Tentative de connexion...');
      // Utilisation de ApiService pour la connexion
      const authData = await ApiService.getInstance().post<AuthResponse>('/api/auth/login', credentials);
      
      logger.debug('[AuthService] Réponse reçue:', JSON.stringify(authData));
      
      if (!authData || !authData.token || !authData.user) {
        logger.error('[AuthService] Structure de réponse invalide:', authData);
        throw new Error('Réponse du serveur invalide');
      }
      
      this.token = authData.token;
      this.user = authData.user;
      
      if (!this.token || !this.user) {
        logger.error('[AuthService] Token ou utilisateur manquant dans la réponse');
        throw new Error('Données d\'authentification incomplètes');
      }
      
      // Stocker token avec les clés standardisées
      StorageUtils.setToken(this.token);
      StorageUtils.setUser(this.user);
      if (authData.refreshToken) {
        localStorage.setItem('refreshToken', authData.refreshToken);
      }
      
      // Mettre à jour l'auth Socket.IO
      socketClusterService.disconnect(); // Forcer la reconnexion avec nouveau token
      
      logger.debug('[AuthService] Connexion réussie pour:', this.user?.email);
      return { token: this.token, user: this.user as User };
    } catch (error: unknown) {
      logger.error('Login error:', error);
      const err = error as { status?: string; message?: string; response?: { status: number; data?: { message?: string } } };

      // 1. Gérer le format ApiError (renvoyé par ApiService via handleApiError)
      if (err && err.status === 'error' && err.message) {
        throw new Error(err.message);
      }

      // 2. Fallback pour Axios (si ApiService n'a pas intercepté ou si utilisé directement)
      if (err.response) {
        const status = err.response.status;
        const data = err.response.data;

        if (status === 401) {
          throw new Error(data?.message || 'Email ou mot de passe incorrect');
        } else if (status === 429) {
          throw new Error('Trop de tentatives. Veuillez réessayer plus tard.');
        } else if (status >= 500) {
          throw new Error('Erreur du serveur. Veuillez réessayer plus tard.');
        }
        throw new Error(data?.message || `Erreur de connexion (${status})`);
      }

      // 3. Fallback final
      throw new Error((error as Error).message || 'Une erreur inattendue s\'est produite');
    }
  }

  // Inscription utilisateur
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      logger.debug('[AuthService] Tentative d\'inscription...');
      const response = await ApiService.getInstance().post<AuthResponse>('/api/auth/register', userData);
      
      this.token = response.token;
      this.user = response.user;
      
      // Stocker token avec les clés standardisées
      StorageUtils.setToken(this.token);
      StorageUtils.setUser(this.user as User);
      if (response.refreshToken) {
        localStorage.setItem('refreshToken', response.refreshToken);
      }
      
      // Mettre à jour l'auth Socket.IO
      socketClusterService.disconnect(); // Forcer la reconnexion avec nouveau token
      
      return response;
    } catch (error: unknown) {
      logger.error('Register error:', error);
      const err = error as { status?: string; message?: string; response?: { status: number; data?: { message?: string } } };

      // 1. Gérer le format ApiError (renvoyé par ApiService via handleApiError)
      if (err && err.status === 'error' && err.message) {
        throw new Error(err.message);
      }

      // 2. Fallback pour Axios
      if (err.response) {
        const status = err.response.status;
        const data = err.response.data;

        if (status === 409) {
          throw new Error('Cet email est déjà utilisé');
        } else if (status === 400) {
          throw new Error(data?.message || 'Données d\'inscription invalides');
        } else if (status === 429) {
          throw new Error('Trop de tentatives. Veuillez réessayer plus tard.');
        } else if (status >= 500) {
          throw new Error('Erreur du serveur. Veuillez réessayer plus tard.');
        }
        throw new Error(data?.message || `Erreur d'inscription (${status})`);
      }

      throw new Error((error as Error).message || 'Une erreur inattendue s\'est produite lors de l\'inscription');
    }
  }

  // Récupérer l'utilisateur actuel
  async getCurrentUser(): Promise<User | null> {
    if (this.currentUserPromise) {
      logger.debug('[AuthService] Réutilisation de la promesse active getCurrentUser...');
      return this.currentUserPromise;
    }

    this.currentUserPromise = (async () => {
      try {
        if (!this.token) {
          const storedToken = StorageUtils.getToken();
          if (storedToken) {
            this.token = storedToken;
          } else {
            return null;
          }
        }

        logger.debug('[AuthService] Appel API /api/auth/me...');
        const response = await ApiService.getInstance().get<{ user: User }>('/api/auth/me');
        logger.debug('[AuthService] Réponse /api/auth/me reçue:', !!response.user);
        
        this.user = response.user;
        return this.user;
      } catch (error: unknown) {
        logger.error('[AuthService] Erreur récupération utilisateur:', error);
        
        if (error && typeof error === 'object' && 'response' in error) {
          const apiError = error as { response?: { status?: number } };
          if (apiError.response?.status === 401) {
            logger.warn('[AuthService] Token expiré ou invalide');
            this.logout();
            throw new Error('Token expired');
          }
        }
        
        return null;
      } finally {
        this.currentUserPromise = null;
      }
    })();

    return this.currentUserPromise;
  }


  // Déconnexion
  async logout(): Promise<void> {
    this.isLoggingOut = true;

    try {
      // ✅ SIMPLIFIÉ: Appel direct sans logique conditionnelle complexe
      // L'intercepteur ApiService gère automatiquement l'exclusion du token
      await ApiService.getInstance().post('/api/auth/logout', {});
    } catch {
      // ✅ SIMPLIFIÉ: Ignorer toutes les erreurs logout (400/401/500)
      // Le backend nettoie toujours les cookies côté serveur
      logger.debug('Logout API call completed (errors ignored as expected)');
    } finally {
      // ✅ PRÉSERVÉ: Nettoyage état local toujours exécuté
      this.token = null;
      this.user = null;
      StorageUtils.clearAll();
      localStorage.removeItem('refreshToken');
      socketClusterService.disconnect(); // Déconnecter le socket
      this.isLoggingOut = false;
    }
  }

  // Mot de passe oublié
  async forgotPassword(email: string): Promise<void> {
    try {
      await ApiService.getInstance().post('/api/auth/password/forgot', { email });
    } catch (error) {
      logger.error('Forgot password error:', error);
      throw error;
    }
  }

  // Réinitialiser mot de passe
  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await ApiService.getInstance().post('/api/auth/password/reset', {
        token,
        newPassword
      });
    } catch (error) {
      logger.error('Reset password error:', error);
      throw error;
    }
  }

  // Changer mot de passe
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await ApiService.getInstance().post('/api/auth/change-password', {
        currentPassword,
        newPassword
      });
    } catch (error) {
      logger.error('Change password error:', error);
      throw error;
    }
  }

  // MFA - Configuration
  async setupMFA(): Promise<MFASetupResponse> {
    try {
      const response = await ApiService.getInstance().post<MFASetupResponse>('/api/auth/mfa/setup', {});
      return response;
    } catch (error) {
      logger.error('MFA setup error:', error);
      throw error;
    }
  }

  // MFA - Activation
  async enableMFA(token: string): Promise<void> {
    try {
      await ApiService.getInstance().post('/api/auth/mfa/enable', { token });
    } catch (error) {
      logger.error('MFA enable error:', error);
      throw error;
    }
  }

  // MFA - Vérification
  async verifyMFA(token: string): Promise<AuthResponse> {
    try {
      const response = await ApiService.getInstance().post<AuthResponse>('/api/auth/mfa/verify', { token });
      this.token = response.token;
      this.user = response.user;
      return response;
    } catch (error) {
      logger.error('MFA verify error:', error);
      throw error;
    }
  }

  // MFA - Désactivation
  async disableMFA(): Promise<void> {
    try {
      await ApiService.getInstance().post('/api/auth/mfa/disable', {});
    } catch (error) {
      logger.error('MFA disable error:', error);
      throw error;
    }
  }

  // Getters
  getToken(): string | null {
    return this.token || StorageUtils.getToken();
  }

  getUser(): User | null {
    if (!this.user) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        this.user = JSON.parse(storedUser);
      }
    }
    return this.user;
  }

  // Méthode pour compatibilité avec AuthContext
  getStoredData(): { token: string | null; user: User | null; expiresAt: number } {
    return {
      token: this.getToken(),
      user: this.getUser(),
      expiresAt: Date.now() + (15 * 60 * 1000)
    };
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  hasRole(role: string): boolean {
    const user = this.getUser();
    return user?.role === role;
  }

  isAdmin(): boolean {
    return this.hasRole('ADMIN');
  }

  isDoctor(): boolean {
    return this.hasRole('DOCTOR');
  }

  isPatient(): boolean {
    return this.hasRole('PATIENT');
  }

  // Méthode pour vérifier la validité du token et récupérer l'utilisateur courant
  // - Retourne { user, csrfToken } si le token est valide
  // - Retourne null si le backend répond 401 (token invalide/expiré)
  // - Lève une erreur pour les autres cas (réseau, 429, 5xx...) afin que l'appelant puisse décider
  async verifyToken(): Promise<{ user: User; csrfToken?: string } | null> {
    try {
      logger.debug('[AuthService] Verifying token via /api/auth/verify...');

      const response = await ApiService.getInstance().get<{
        status?: string;
        user: User;
        message?: string;
        csrfToken?: string;
      }>('/api/auth/verify');

      if (response && response.user) {
        this.user = response.user;
        return { user: response.user, csrfToken: response.csrfToken };
      }

      return null;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;

      if (status === 401) {
        logger.warn('[AuthService] verifyToken: token invalide ou expiré (401)');
        return null;
      }

      logger.error('[AuthService] verifyToken failed (non-401):', error);
      throw error;
    }
  }

  // Méthode pour rafraîchir le token
  async refreshToken(): Promise<AuthResponse | null> {
    if (this.isRefreshing) {
      logger.debug('[AuthService] Refresh already in progress, skipping');
      return null;
    }

    this.isRefreshing = true;

    logger.debug('[AuthService] Attempting to refresh token via /api/auth/refresh...');

    try {
      // Le backend lit le refreshToken depuis les cookies HttpOnly
      const response = await ApiService.getInstance().post<AuthResponse>('/api/auth/refresh', {});

      if (response && response.token && response.user) {
        this.token = response.token;
        this.user = response.user;

        StorageUtils.setToken(response.token);
        StorageUtils.setUser(response.user);

        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }

        socketClusterService.setAuthToken(response.token);

        logger.info('[AuthService] Token refreshed successfully.');
        return response;
      }

      throw new Error('Invalid response from refresh token endpoint');
    } catch (error: unknown) { 
      logger.error('[AuthService] Token refresh failed:', error);

      // En cas d'erreur 401 ou autre échec critique, forcer la déconnexion
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        logger.warn('[AuthService] Refresh token invalid or expired, logging out user');
      }
      this.logout();
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }
}

// Export singleton
const authService = AuthService.getInstance();
export default authService;
export { authService };
